import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAllEmployees } from '~/data/employees';
import { Employees } from '~/data/employees/types';
import { useAllOrgUnits } from '~/data/orgunits';
import { OrganizationUnit } from '~/data/orgunits/types';
import { UserActivityEvent } from '~/data/userActivity';
import {
  UserActivityEventPayloadDtoTarget,
  UserActivityEventPayloadDtoType,
} from '~/services/Http/client.iam.generated';

type EmployeeAndUnit = {
  employee: Employees | undefined;
  unit: OrganizationUnit | undefined;
};

type ResolveEmployeeAndUnit = (employeeId: number | string) => EmployeeAndUnit;

export type UserActivityGridDataRow = {
  id: number;
  userName: string;
  userUnit: string;
  eventType: string;
  resourceName: string;
  targetType: string;
  targetName: string;
  serverTime: Date;
  clientTime: Date;
  ipAddress: string;
  userAgent: string;
  meta: {
    eventData: UserActivityEvent;
    subjectData: EmployeeAndUnit;
  };
};

export type UseUserActivityGridDataOutput = {
  gridData: UserActivityGridDataRow[];
  resolveEmployeeAndUnit: ResolveEmployeeAndUnit;
};

type PropsType = {
  userActivity: UserActivityEvent[];
};

export const useUserActivityLogGridData = (
  props: PropsType,
): UseUserActivityGridDataOutput => {
  const { userActivity } = props;
  const { t } = useTranslation();
  const { allEmployees } = useAllEmployees();
  const { orgUnitsIncludeDeleted } = useAllOrgUnits();

  const findUnit = useCallback(
    (unitId: number): OrganizationUnit | undefined => {
      return orgUnitsIncludeDeleted.find((unit) => unit.fOrgUnitId === unitId);
    },
    [orgUnitsIncludeDeleted],
  );

  const findEmployee = useCallback(
    (employeeId: string | number): Employees | undefined => {
      const employeeIdNumber: number =
        typeof employeeId === 'number' ? employeeId : parseInt(employeeId);

      return Object.values(allEmployees).find((employee) => {
        return employee.fEmpId === employeeIdNumber;
      });
    },
    [allEmployees],
  );
  const getEmployeeName = useCallback((employee: Employees) => {
    return `${employee.fEmpFirstName} ${employee.fEmpLastName}`;
  }, []);

  const resolveEmployeeAndUnit: ResolveEmployeeAndUnit = useCallback(
    (employeeId: number | string) => {
      const foundEmployee = findEmployee(employeeId);
      const foundEmployeeUnit =
        (!!foundEmployee && findUnit(foundEmployee.fEmpUnit)) || undefined;

      return {
        employee: foundEmployee,
        unit: foundEmployeeUnit,
      };
    },
    [findEmployee, findUnit],
  );

  const resolveTargetName = useCallback(
    (event: UserActivityEvent) => {
      const target = event.data?.target;
      const targetId = event.data?.targetId;

      if (!target || !targetId) {
        return `Unknown`;
      }

      switch (target) {
        case UserActivityEventPayloadDtoTarget.Employee:
          const empInfo = resolveEmployeeAndUnit(targetId);

          return (
            (!!empInfo.employee && getEmployeeName(empInfo.employee)) || ''
          );
        case UserActivityEventPayloadDtoTarget.Unit:
          const foundUnit = findUnit(parseInt(targetId));
          return `Unit: ${foundUnit?.fOrgUnitName}`;
        case UserActivityEventPayloadDtoTarget.Company:
          return `Company: ${targetId}`;
        default:
          return `Unknown: ${targetId}`;
      }
    },
    [findUnit, getEmployeeName, resolveEmployeeAndUnit],
  );

  const translateEventType = useCallback(
    (type: UserActivityEventPayloadDtoType | undefined) => {
      const translationKeys: {
        [key in UserActivityEventPayloadDtoType]: string;
      } = {
        [UserActivityEventPayloadDtoType.Read]: 'userActivityTypeRead',
        [UserActivityEventPayloadDtoType.Write]: 'userActivityTypeWrite',
      };

      return !!type ? t(translationKeys[type]) : '';
    },
    [t],
  );

  const translateTargetType = useCallback(
    (type: UserActivityEventPayloadDtoTarget | undefined): string => {
      const translationKeys: {
        [key in UserActivityEventPayloadDtoTarget]: string;
      } = {
        [UserActivityEventPayloadDtoTarget.Employee]: 'employee',
        [UserActivityEventPayloadDtoTarget.Unit]: 'laUnit',
        [UserActivityEventPayloadDtoTarget.Company]: 'laCompany',
        [UserActivityEventPayloadDtoTarget.Other]: 'laOther',
      };

      return !!type ? t(translationKeys[type]) : '';
    },
    [t],
  );

  const gridData = useMemo(() => {
    return userActivity.map((event): UserActivityGridDataRow => {
      // TODO: Get EmployeeId from UserId
      const subjectEmployeeId = !!event.subject.employeeId
        ? event.subject.employeeId
        : 0;
      const subjectData = resolveEmployeeAndUnit(subjectEmployeeId);

      return {
        id: event.id,
        userName:
          (!!subjectData.employee && getEmployeeName(subjectData.employee)) ||
          '',
        userUnit: (!!subjectData.unit && subjectData.unit.fOrgUnitName) || '',
        eventType: translateEventType(event.data?.type),
        resourceName: event.data?.resourceName || '',
        targetType: translateTargetType(event.data?.target),
        targetName: resolveTargetName(event),
        serverTime: new Date(event.serverTimestamp),
        clientTime: new Date(event.clientTimestamp),
        ipAddress: event.data?.ipAddress || '',
        userAgent: event.data?.userAgent || '',
        meta: {
          eventData: event,
          subjectData,
        },
      };
    });
  }, [
    userActivity,
    resolveTargetName,
    getEmployeeName,
    resolveEmployeeAndUnit,
    translateEventType,
    translateTargetType,
  ]);

  return {
    gridData,
    resolveEmployeeAndUnit,
  };
};
